
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { firstValueFrom, map } from 'rxjs';
import {
  ContactCreateRequest,
  ContactCreateResponse,
  ContactDetailResponse,
  ContactUpdateRequest,
  ContactUpdateResponse,
  PaginatedContactResponse
} from '../../domain/contact.domain';
import { ContactRepository } from '../../domain/contact.repository';
import { BcpEphemeralKeyManager, BcpHttpClient } from '@cix-software-labs/ng-networking';
import { Country } from '@app/shared/constants/country.constants';

/**
 * Contact HTTP Repository
 *
 * @implements {ContactRepository}
 */
@Injectable()
export class ContactHttpRepository extends ContactRepository {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = `${environment.API_URL_BASE}contacts`;
  private readonly _keyManager = inject(BcpEphemeralKeyManager);
  private readonly _httpClient = inject(BcpHttpClient);

  async getContacts(
    page: number,
    pageSize: number
  ): Promise<PaginatedContactResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', pageSize.toString());

    return firstValueFrom(
      this._http.get<PaginatedContactResponse>(this._apiUrl, { params })
    );
  }

  async getContactById(id: string): Promise<ContactDetailResponse> {
    return firstValueFrom(
      this._http.get<ContactDetailResponse>(`${this._apiUrl}/${id}`)
    );
  }

  private getEncryptFieldsByCountryCode(countryCode: string): string[] {
    const baseEncryptFields = ['contactBankAccountNumber','contactDocumentNumber', 'contactGivenNames', 'contactPaternalLastName', 'contactMaternalLastName', 'fullContactAddress' ];
    const extendedEncryptFields = [
      ...baseEncryptFields,
      'contactBankAccountType',
      'contactPhoneNumber',
      'contactEmail'
    ];

    const countriesWithExtendedEncryption = [Country.COL, Country.CHL, Country.ARG];
    return countriesWithExtendedEncryption.includes(countryCode as Country)
      ? extendedEncryptFields
      : baseEncryptFields;
  }

  public async createContact(data: ContactCreateRequest): Promise<ContactCreateResponse> {
    await this._keyManager.generate();
    const encryptFields = this.getEncryptFieldsByCountryCode(data.contactCountryCode);
     return firstValueFrom(
          this._httpClient
            .post<ContactCreateResponse>(this._apiUrl, data, {
              encrypt: encryptFields,
            })
            .pipe(
              map((response) => {
                return response;
              })
            ) 
        );
  }

   public async updateContact(id: string, data: ContactUpdateRequest): Promise<ContactUpdateResponse> {
    await this._keyManager.generate();
    const encryptFields = this.getEncryptFieldsByCountryCode(data.contactCountryCode);
     return firstValueFrom(
          this._httpClient
            .put<ContactUpdateResponse>(`${this._apiUrl}/${id}`, data, {
              encrypt: encryptFields,
            })
            .pipe(
              map((response) => {
                return response;
              })
            ) 
        );
  }

  async deleteContact(id: string): Promise<void> {
    return firstValueFrom(
      this._http.delete<void>(`${this._apiUrl}/${id}`)
    );
  }
}
